import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { Toaster } from 'react-hot-toast'

import App from './App.tsx'
import { msalConfig } from '@/config/auth'
import './index.css'

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig)

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Initialize MSAL and handle redirect before rendering
msalInstance.initialize().then(() => {
  if (import.meta.env.DEV) console.log('✅ MSAL initialized successfully')

  // Handle redirect promise to complete any pending auth flows.
  // Race against a 10s safety timeout so the app still renders if
  // handleRedirectPromise() hangs (e.g. stale MSAL cache from a
  // previously-closed tab).  Clear the timeout when the redirect promise
  // settles first so the warning only fires when the timeout genuinely
  // won the race — otherwise it logs spurious noise on every normal
  // page load.
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined
  const redirectPromise = msalInstance
    .handleRedirectPromise()
    .finally(() => {
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle)
    })
  const timeoutPromise = new Promise<null>((resolve) => {
    timeoutHandle = setTimeout(() => {
      console.warn('⚠️ MSAL handleRedirectPromise timed out after 10s — rendering app anyway')
      resolve(null)
    }, 10000)
  })
  return Promise.race([redirectPromise, timeoutPromise])
}).then((tokenResponse) => {
  if (tokenResponse) {
    if (import.meta.env.DEV) console.log('✅ Redirect authentication completed')
    
    // Check if state contains the returnUrl
    if (tokenResponse.state) {
      if (import.meta.env.DEV) console.log('🔄 Restoring URL from MSAL state:', tokenResponse.state)
      localStorage.setItem('returnUrl', tokenResponse.state)
    }
  }
  
  // Render app only after MSAL is fully initialized and redirects handled
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <App />
            <Toaster
              position="top-right"
              containerStyle={{
                top: 80,
              }}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  )
}).catch((error) => {
  console.error('❌ MSAL initialization or redirect handling failed:', error)
  
  // Render a basic error page if MSAL fails completely
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>Authentication Error</h1>
      <p>Failed to initialize authentication system. Please refresh the page.</p>
      <button onClick={() => window.location.reload()}>Refresh Page</button>
    </div>
  )
})
